import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { tasksAPI } from '@/features/admin/tabs/mes-taches/api/tasks.api';
import { TaskFormData, User, Task } from '@/features/admin/tabs/mes-taches/types/task.types';
import { Avatar } from '@/features/admin/tabs/mes-taches/components/Avatar';
// Simple icon components to avoid dependency issues
const CalendarIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const BuildingOffice2Icon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const UserGroupIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const AcademicCapIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
  </svg>
);

const ClipboardDocumentListIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const PaperClipIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
  </svg>
);

const XMarkIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
import './TaskCreationModal.styles.css';

interface TaskCreationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  title?: string;
  showDirectForm?: boolean;
  editingTask?: Task | null;
}

type Priority = 'normal' | 'urgent' | 'critique';
type AssignmentType = 'bureau' | 'expert' | 'users';

const priorityMapping: Record<string, Priority> = {
  'low': 'normal',
  'medium': 'normal',
  'high': 'urgent',
  'urgent': 'urgent',
  'critique': 'critique'
};

const reversePriorityMapping: Record<Priority, 'low' | 'medium' | 'high' | 'urgent'> = {
  'normal': 'medium',
  'urgent': 'urgent',
  'critique': 'urgent' // Map critique to urgent for backend compatibility
};

export const TaskCreationModal: React.FC<TaskCreationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  title = 'Nouvelle tâche',
  showDirectForm = true,
  editingTask = null
}) => {
  const [users, setUsers] = useState<User[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(showDirectForm);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<TaskFormData>({
    message: '',
    assignedToUsers: [],
    assignedToRoles: [],
    deadline: '',
    criticity: 'medium'
  });
  const [assignmentType, setAssignmentType] = useState<AssignmentType>('users');
  const [priority, setPriority] = useState<Priority>('normal');
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof TaskFormData, string>>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Reset states when modal opens
      setShowCreateForm(showDirectForm || !!editingTask);
      setError(null);
      setSuccessMessage(null);
      setIsLoading(false);

      // If editing, pre-fill form with task data
      if (editingTask) {
        // Determine assignment type based on task data
        let assignType: AssignmentType = 'users';
        if (editingTask.assigned_roles?.includes('bureau')) {
          assignType = 'bureau';
        } else if (editingTask.assigned_roles?.includes('expert')) {
          assignType = 'expert';
        }

        setFormData({
          message: editingTask.message || '',
          assignedToUsers: editingTask.assigned_user_ids || [],
          assignedToRoles: editingTask.assigned_roles || [],
          deadline: editingTask.deadline ? editingTask.deadline.split('T')[0] : '',
          criticity: editingTask.criticity || 'medium'
        });
        setAssignmentType(assignType);
        setPriority(priorityMapping[editingTask.criticity] || 'normal');
      } else {
        // Reset form for new task
        setFormData({
          message: '',
          assignedToUsers: [],
          assignedToRoles: [],
          deadline: '',
          criticity: 'medium'
        });
        setAssignmentType('users');
        setPriority('normal');
      }

      setFormErrors({});
      setSearchTerm('');
      setShowUserDropdown(false);
    }
  }, [isOpen, showDirectForm, editingTask]);

  // Keyboard shortcuts and click outside
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        e.preventDefault();
        if (showUserDropdown) {
          setShowUserDropdown(false);
        } else if (!isSubmitting) {
          onClose();
        }
      }

      if (showCreateForm && e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    };

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.task-creation-modal-user-search-wrapper')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showCreateForm, showUserDropdown, isSubmitting, onClose]);

  const fetchUsers = async () => {
    try {
      const response = await tasksAPI.getAllUsers();
      if (response.success) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // Validation
    const newErrors: Partial<Record<keyof TaskFormData, string>> = {};
    
    if (!formData.message.trim()) {
      newErrors.message = 'Le message est requis';
    }

    if (assignmentType === 'users' && (formData.assignedToUsers?.length || 0) === 0) {
      newErrors.assignedToUsers = 'Veuillez sélectionner au moins un utilisateur';
    }

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      // Scroll to first error
      const firstErrorElement = document.querySelector('.task-creation-modal-error-message');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setError(null);
    
    try {
      // Map priority back to backend format
      const submitData: TaskFormData = {
        ...formData,
        criticity: reversePriorityMapping[priority]
      };

      let response: { success: boolean; data: Task };
      if (editingTask) {
        // Update existing task
        response = await tasksAPI.updateTask(editingTask.id, submitData);
        if (response.success) {
          setSuccessMessage('Tâche modifiée avec succès');
        }
      } else {
        // Create new task
        response = await tasksAPI.createTask(submitData);
        if (response.success) {
          setSuccessMessage('Tâche créée avec succès');
        }
      }

      if (response.success) {
        onSuccess?.();

        // Close the modal after success
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (error) {
      console.error(`Error ${editingTask ? 'updating' : 'creating'} task:`, error);
      setError(`Erreur lors de la ${editingTask ? 'modification' : 'création'} de la tâche`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleAssignmentTypeChange = (type: AssignmentType) => {
    setAssignmentType(type);
    if (type === 'bureau') {
      setFormData(prev => ({
        ...prev,
        assignedToRoles: ['bureau'],
        assignedToUsers: []
      }));
    } else if (type === 'expert') {
      setFormData(prev => ({
        ...prev,
        assignedToRoles: ['expert'],
        assignedToUsers: []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        assignedToRoles: [],
        assignedToUsers: []
      }));
    }
    setFormErrors(prev => ({ ...prev, assignedToUsers: undefined }));
  };

  const handleUserToggle = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      assignedToUsers: prev.assignedToUsers?.includes(userId)
        ? prev.assignedToUsers.filter(id => id !== userId)
        : [...(prev.assignedToUsers || []), userId]
    }));
    setFormErrors(prev => ({ ...prev, assignedToUsers: undefined }));
  };

  const removeUser = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      assignedToUsers: prev.assignedToUsers?.filter(id => id !== userId) || []
    }));
  };

  const handlePriorityChange = (newPriority: Priority) => {
    setPriority(newPriority);
    setFormData(prev => ({
      ...prev,
      criticity: reversePriorityMapping[newPriority]
    }));
  };

  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase()) || 
           user.email.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const selectedUsers = users.filter(u => formData.assignedToUsers?.includes(u.id));

  if (!isOpen) return null;

  return createPortal(
    <div className="task-creation-modal-overlay" onClick={handleOverlayClick}>
      <div 
        className="task-creation-modal" 
        onClick={handleModalClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <div className="task-creation-modal-header">
          <h2 id="modal-title">{title}</h2>
          <button
            className="task-creation-modal-close"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Fermer la fenêtre"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="task-creation-modal-body">
          {error && (
            <div className="task-creation-modal-error" role="alert">
              {error}
            </div>
          )}

          {successMessage && (
            <div className="task-creation-modal-success" role="alert">
              {successMessage}
            </div>
          )}

          {(
                <div className="task-creation-modal-form-section">
                  <h3>{editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}</h3>
                  
                  {/* Attribution Section */}
                  <div className="task-creation-modal-attribution">
                    <label className="task-creation-modal-attribution-label">
                      Attribution *
                    </label>
                    <div className="task-creation-modal-attribution-buttons">
                      <button
                        type="button"
                        className={`task-creation-modal-attribution-btn ${assignmentType === 'users' ? 'selected' : ''}`}
                        onClick={() => handleAssignmentTypeChange('users')}
                        disabled={isSubmitting}
                        aria-pressed={assignmentType === 'users'}
                      >
                        <UserGroupIcon className="icon" />
                        Utilisateurs
                      </button>
                      <button
                        type="button"
                        className={`task-creation-modal-attribution-btn ${assignmentType === 'bureau' ? 'selected' : ''}`}
                        onClick={() => handleAssignmentTypeChange('bureau')}
                        disabled={isSubmitting}
                        aria-pressed={assignmentType === 'bureau'}
                      >
                        <BuildingOffice2Icon className="icon" />
                        Bureau
                      </button>
                      <button
                        type="button"
                        className={`task-creation-modal-attribution-btn ${assignmentType === 'expert' ? 'selected' : ''}`}
                        onClick={() => handleAssignmentTypeChange('expert')}
                        disabled={isSubmitting}
                        aria-pressed={assignmentType === 'expert'}
                      >
                        <AcademicCapIcon className="icon" />
                        Expert
                      </button>
                    </div>

                    {/* User Selection - Only show when users is selected */}
                    {assignmentType === 'users' && (
                      <div className="task-creation-modal-user-selection">
                        <div className="task-creation-modal-user-search-wrapper">
                          <input
                            type="text"
                            className="task-creation-modal-message-textarea"
                            style={{ minHeight: '44px', resize: 'none' }}
                            placeholder="Rechercher et ajouter des utilisateurs..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setShowUserDropdown(true)}
                            disabled={isSubmitting}
                            aria-label="Rechercher des utilisateurs"
                          />
                          
                          {/* User Dropdown */}
                          {showUserDropdown && searchTerm && (
                            <div 
                              className="task-creation-modal-user-dropdown"
                              role="listbox"
                              aria-label="Résultats de recherche d'utilisateurs"
                            >
                              {filteredUsers.length > 0 ? (
                                filteredUsers.slice(0, 5).map(user => (
                                  <div
                                    key={user.id}
                                    className={`task-creation-modal-user-option ${formData.assignedToUsers?.includes(user.id) ? 'selected' : ''}`}
                                    onClick={() => {
                                      handleUserToggle(user.id);
                                      setSearchTerm('');
                                      setShowUserDropdown(false);
                                    }}
                                    role="option"
                                    aria-selected={formData.assignedToUsers?.includes(user.id)}
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleUserToggle(user.id);
                                        setSearchTerm('');
                                        setShowUserDropdown(false);
                                      }
                                    }}
                                  >
                                    <Avatar name={`${user.first_name} ${user.last_name}`} size="small" />
                                    <div className="task-creation-modal-user-option-info">
                                      <span className="task-creation-modal-user-option-name">
                                        {user.first_name} {user.last_name}
                                      </span>
                                      <span className="task-creation-modal-user-option-email">{user.email}</span>
                                    </div>
                                    {formData.assignedToUsers?.includes(user.id) && (
                                      <span className="task-creation-modal-option-check">✓</span>
                                    )}
                                  </div>
                                ))
                              ) : (
                                <div className="task-creation-modal-no-results">Aucun utilisateur trouvé</div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Selected Users */}
                        {selectedUsers.length > 0 && (
                          <div className="task-creation-modal-selected-users">
                            {selectedUsers.map(user => (
                              <div key={user.id} className="task-creation-modal-user-chip">
                                <Avatar name={`${user.first_name} ${user.last_name}`} size="small" />
                                <span className="task-creation-modal-user-chip-name">
                                  {user.first_name} {user.last_name}
                                </span>
                                <button
                                  type="button"
                                  className="remove-btn"
                                  onClick={() => removeUser(user.id)}
                                  disabled={isSubmitting}
                                  aria-label={`Retirer ${user.first_name} ${user.last_name}`}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {formErrors.assignedToUsers && (
                          <span className="task-creation-modal-error-message">{formErrors.assignedToUsers}</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Message Section */}
                  <div className="task-creation-modal-message-group">
                    <label htmlFor="task-message" className="task-creation-modal-message-label">
                      Message de la tâche *
                    </label>
                    <textarea
                      id="task-message"
                      className={`task-creation-modal-message-textarea ${formErrors.message ? 'error' : ''}`}
                      value={formData.message}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, message: e.target.value }));
                        setFormErrors(prev => ({ ...prev, message: undefined }));
                      }}
                      placeholder="Décrivez la tâche à réaliser..."
                      disabled={isSubmitting}
                      aria-describedby={formErrors.message ? "message-error" : undefined}
                    />
                    {formErrors.message && (
                      <span id="message-error" className="task-creation-modal-error-message">{formErrors.message}</span>
                    )}
                  </div>

                  {/* Priority and Date Row */}
                  <div className="task-creation-modal-form-row">
                    {/* Priority Pills */}
                    <div className="task-creation-modal-priority-group">
                      <span className="task-creation-modal-priority-label">Priorité</span>
                      <div className="task-creation-modal-priority-pills" role="radiogroup" aria-label="Sélectionner la priorité">
                        {(['normal', 'urgent', 'critique'] as const).map((level) => (
                          <button
                            key={level}
                            type="button"
                            className={`task-creation-modal-priority-pill ${level} ${priority === level ? 'selected' : ''}`}
                            onClick={() => handlePriorityChange(level)}
                            disabled={isSubmitting}
                            role="radio"
                            aria-checked={priority === level}
                          >
                            <span 
                              className="task-creation-modal-priority-dot"
                              style={{
                                backgroundColor: 
                                  level === 'critique' ? '#DC2626' :
                                  level === 'urgent' ? '#F97316' : '#6B7280'
                              }}
                            />
                            {level === 'critique' ? 'Critique' :
                             level === 'urgent' ? 'Urgent' : 'Normal'}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Date Input */}
                    <div className="task-creation-modal-date-group">
                      <label htmlFor="task-deadline" className="task-creation-modal-date-label">
                        Échéance
                      </label>
                      <div className="task-creation-modal-date-wrapper">
                        <input
                          id="task-deadline"
                          type="date"
                          className="task-creation-modal-date-input"
                          value={formData.deadline}
                          onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                          min={format(new Date(), 'yyyy-MM-dd')}
                          disabled={isSubmitting}
                        />
                        <CalendarIcon className="task-creation-modal-date-icon" />
                      </div>
                    </div>
                  </div>

                  {/* Attachments (Disabled) */}
                  <div className="task-creation-modal-attachments-group">
                    <label className="task-creation-modal-attachments-label">
                      Pièces jointes
                    </label>
                    <div className="task-creation-modal-attachments-placeholder">
                      <PaperClipIcon className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                      <p>Fonctionnalité bientôt disponible</p>
                    </div>
                  </div>

                  {/* Form Actions */}
                  <div className="task-creation-modal-actions">
                    <button
                      type="button"
                      className="task-creation-modal-btn-cancel"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className="task-creation-modal-btn-create"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (editingTask ? 'Modification...' : 'Création...') : (editingTask ? 'Modifier la tâche' : 'Créer la tâche')}
                    </button>
                  </div>
                </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};